import { isSellerPanel } from "./panelConfig";

export const API_PREFIX = "/api/v1";

const byPanel = (adminPath, sellerPath = adminPath) =>
  isSellerPanel() ? sellerPath : adminPath;

const byPanelFn =
  (adminBuilder, sellerBuilder = adminBuilder) =>
  (...args) =>
    isSellerPanel() ? sellerBuilder(...args) : adminBuilder(...args);

export const ENDPOINTS = {
  caAnalytics: {
    base: byPanel("/admin/ca-analytics", "/sellers/me/ca-analytics"),
  },
  meta: {
    dropdown: (resource) => `/meta/dropdowns/${resource}`,
  },
  brands: {
    submissions: "/platform/brands/submissions",
    mySubmissions: "/platform/brands/submissions/mine",
    submission: (brandId) => `/platform/brands/submissions/${brandId}`,
    approval: (brandId) => `/admin/platform/brands/${brandId}/approval`,
    approvalBulk: "/admin/platform/brands/approval",
  },
  catalogApprovals: {
    category: (categoryKey) =>
      `/admin/platform/categories/${categoryKey}/approval`,
    hsn: (hsnCode) => `/admin/platform/hsn-codes/${hsnCode}/approval`,
  },
  productOptions: {
    submissions: "/platform/product-options/submissions",
    mySubmissions: "/platform/product-options/submissions/mine",
    availableMine: "/platform/product-options/available/mine",
    approval: (optionId) =>
      `/admin/platform/product-options/${optionId}/approval`,
  },
  auth: {
    register: "/auth/register",
    login: "/auth/login",
    social: "/auth/social",
    status: "/auth/status",
    refresh: "/auth/refresh",
    me: "/users/me",
    forgotPassword: "/auth/forgot-password",
    sendOtp: "/auth/send-otp",
    verifyOtp: "/auth/verify-otp",
    resendOtp: "/auth/resend-otp",
    resetPassword: "/auth/reset-password",
    changePassword: "/auth/change-password",
    registerOtp: "/auth/register-otp",
    verifyRegistration: "/auth/verify-registration",
  },
  dashboard: {
    overview: byPanel("/admin/dashboard/overview", "/sellers/me/dashboard"),
  },
  users: {
    adminUsers: "/admin/users",
    adminUser: (userId) => `/admin/users/${userId}`,
    myAddresses: "/users/me/addresses",
    myAddress: (addressId) => `/users/me/addresses/${addressId}`,
  },
  adminAccess: {
    modules: byPanel("/admin/access/modules", "/sellers/me/access/modules"),
    admins: "/admin/access/admins",
    subAdmins: byPanel("/admin/access/sub-admins", "/sellers/me/sub-admins"),
    subAdminModules: byPanelFn(
      (userId) => `/admin/access/sub-admins/${userId}/modules`,
      (userId) => `/sellers/me/sub-admins/${userId}/modules`,
    ),
  },
  sellerUsers: {
    sellers: "/admin/seller-users/sellers",
    sellerAdmins: "/admin/seller-users/seller-admins",
    sellerSubAdmins: "/admin/seller-users/seller-sub-admins",
    createSellerAdmin: "/admin/seller-users/seller-admin",
    createSellerSubAdmin: "/admin/seller-users/seller-sub-admin",
  },
  sellers: {
    myChargeSettings: "/sellers/me/charge-settings",
    onboardingKyc: "/sellers/onboarding/kyc",
    onboardingProfile: "/sellers/onboarding/profile",
    aadhaarPrecheck: "/sellers/kyc/aadhaar/precheck",
    aadhaarSendOtp: "/sellers/kyc/aadhaar/send-otp",
    aadhaarVerifyOtp: "/sellers/kyc/aadhaar/verify-otp",
    panPrecheck: "/sellers/kyc/pan/precheck",
    panVerify: "/sellers/kyc/pan/verify",
    vendors: "/admin/vendors",
    list: "/admin/sellers",
    detail: (sellerId) => `/admin/sellers/${sellerId}`,
    vendorStatus: (sellerId) => `/admin/vendors/${sellerId}/status`,
    adminStatus: (sellerId) => `/admin/sellers/${sellerId}/status`,
    kycStatus: (sellerId) => `/admin/sellers/${sellerId}/kyc/status`,
    kycDetail: (sellerId) => `/admin/sellers/${sellerId}/kyc`,
    bankStatus: (sellerId) => `/admin/sellers/${sellerId}/bank/status`,
    onboardingStatus: (sellerId) =>
      `/admin/sellers/${sellerId}/onboarding/status`,
    goLive: (sellerId) => `/admin/sellers/${sellerId}/go-live`,
    kycReview: (sellerId) => `/sellers/${sellerId}/kyc/review`,
    kycReviewAdmin: (sellerId) => `/admin/sellers/${sellerId}/kyc/status`,
    organizations: (sellerId) => `/admin/sellers/${sellerId}/organizations`,
    organization: (sellerId, organizationId) =>
      `/admin/sellers/${sellerId}/organizations/${organizationId}`,
    organizationStatus: (sellerId, organizationId) =>
      `/admin/sellers/${sellerId}/organizations/${organizationId}/status`,
    myOrganizations: "/sellers/me/organizations",
    myOrganization: (organizationId) =>
      `/sellers/me/organizations/${organizationId}`,
    myOrganizationDefault: (organizationId) =>
      `/sellers/me/organizations/${organizationId}/default`,
    myStatus: "/sellers/me/status",
    myKyc: "/sellers/me/kyc",
    sellerOnboardingStatus: "/sellers/me/onboarding-status",
    approvalModalSeen: "/sellers/me/onboarding/approval-modal-seen",
    tracking: "/sellers/me/tracking",
    trackingOrder: (orderId) => `/sellers/me/tracking/${orderId}`,
    profile: "/sellers/me/profile",
    businessAddress: "/sellers/me/business-address",
    pickupAddress: "/sellers/me/pickup-address",
    bankDetails: "/sellers/me/bank-details",
    moreInfo: "/sellers/me/more-info",
    settings: "/sellers/me/settings",
    dashboard: "/sellers/me/dashboard",
    subAdmins: "/sellers/me/sub-admins",
    subAdminModules: (userId) => `/sellers/me/sub-admins/${userId}/modules`,
    subAdminStatus: (userId) => `/sellers/me/sub-admins/${userId}/status`,
    subAdmin: (userId) => `/sellers/me/sub-admins/${userId}`,
  },
  sellerOrganizations: {
    list: "/admin/seller-organizations",
    create: (sellerId) => `/admin/sellers/${sellerId}/organizations`,
    bySeller: (sellerId) => `/admin/sellers/${sellerId}/organizations`,
    detail: (sellerId, organizationId) =>
      `/admin/sellers/${sellerId}/organizations/${organizationId}`,
    update: (sellerId, organizationId) =>
      `/admin/sellers/${sellerId}/organizations/${organizationId}`,
    status: (sellerId, organizationId) =>
      `/admin/sellers/${sellerId}/organizations/${organizationId}/status`,
  },
  products: {
    list: byPanel("/admin/products", "/products"),
    listForPanel: byPanel("/admin/products", "/products/seller/me"),
    prefill: byPanel("/admin/products/prefill", "/products/prefill"),
    prefillBasic: byPanel(
      "/admin/products/prefill/basic",
      "/products/prefill/basic",
    ),
    prefillLookups: byPanel(
      "/admin/products/prefill/lookups",
      "/products/prefill/lookups",
    ),
    prefillLocations: byPanel(
      "/admin/products/prefill/locations",
      "/products/prefill/locations",
    ),
    prefillProducts: byPanel(
      "/admin/products/prefill/products",
      "/products/prefill/products",
    ),
    search: "/products/search",
    sellerMine: "/products/seller/me",
    detail: byPanelFn(
      (productId) => `/admin/products/${productId}`,
      (productId) => `/products/manage/${productId}`,
    ),
    moderate: (productId) => `/admin/products/${productId}/moderate`,
    revisions: byPanelFn(
      (productId) => `/admin/products/${productId}/revisions`,
      (productId) => `/products/${productId}/revisions`,
    ),
    reviewRevision: (productId, revisionId) =>
      `/admin/products/${productId}/revisions/${revisionId}/review`,
    approve: (productId) => `/admin/products/${productId}/approve`,
    reject: (productId) => `/admin/products/${productId}/reject`,
    status: byPanelFn(
      (productId) => `/admin/products/${productId}/status`,
      (productId) => `/products/${productId}/status`,
    ),
    permanentDelete: (productId) => `/admin/products/${productId}/permanent`,
    duplicate: byPanelFn(
      (productId) => `/admin/products/${productId}/duplicate`,
      (productId) => `/products/${productId}/duplicate`,
    ),
    bulkUpdate: byPanel("/admin/products/bulk/update", "/products/bulk/update"),
    specialPrices: byPanel(
      "/admin/products/special-prices/bulk",
      "/products/special-prices/bulk",
    ),
    inventory: byPanelFn(
      (productId) => `/admin/products/${productId}/inventory`,
      (productId) => `/products/${productId}/inventory`,
    ),
    inventoryStats: byPanel(
      "/admin/products/inventory/stats",
      "/products/inventory/stats",
    ),
    inventoryLowStock: byPanel(
      "/admin/inventory/low-stock",
      "/products/inventory/low-stock",
    ),
    analyticsTop: byPanel(
      "/admin/products/analytics/top",
      "/products/analytics/top",
    ),
  },
  orders: {
    admin: "/admin/orders",
    mine: "/orders/me",
    sellerMine: "/orders/seller/me",
    listForPanel: byPanel("/admin/orders", "/orders/seller/me"),
    create: "/orders",
    detail: (orderId) => `/orders/${orderId}`,
    cancellationPreview: (orderId) => `/orders/${orderId}/cancellation-preview`,
    cancel: (orderId) => `/orders/${orderId}/cancel`,
    status: (orderId) => `/orders/${orderId}/status`,
    notes: (orderId) => `/orders/${orderId}/notes`,
    boxLabelDownload: (orderId, shipmentId) =>
      `/orders/${orderId}/shipments/${shipmentId}/box-label`,
    checkoutQuote: "/orders/checkout/admin-quote",
  },
  cancellations: {
    list: "/cancellations",
    detail: (cancellationId) => `/cancellations/${cancellationId}`,
    approve: (cancellationId) => `/cancellations/${cancellationId}/approve`,
    reject: (cancellationId) => `/cancellations/${cancellationId}/reject`,
    retry: (cancellationId) => `/cancellations/${cancellationId}/retry`,
    approveRefund: (cancellationId) =>
      `/cancellations/${cancellationId}/approve-refund`,
    manualRefund: (cancellationId) =>
      `/cancellations/${cancellationId}/manual-refund`,
  },
  carts: {
    admin: "/admin/carts",
    detail: (cartId) => `/admin/carts/${cartId}`,
  },
  payments: {
    admin: "/payments/admin",
    detail: (paymentId) => `/payments/admin/${paymentId}`,
    options: "/payments/options",
    approve: (paymentId) => `/payments/${paymentId}/approve`,
    reject: (paymentId) => `/payments/${paymentId}/reject`,
    codCollections: "/payments/cod-collections",
    myCodCollections: "/payments/cod-collections/mine",
    submitCodCollection: (shipmentId) =>
      `/payments/cod-collections/shipments/${shipmentId}/submit`,
    confirmCodCollection: (shipmentId) =>
      `/payments/cod-collections/shipments/${shipmentId}/confirm`,
    verifyCodCollection: (collectionId) =>
      `/payments/cod-collections/${collectionId}/verify`,
  },
  inventory: {
    variants: byPanel(
      "/admin/inventory/variants",
      "/products/inventory/variants",
    ),
    product: byPanelFn(
      (productId) => `/admin/inventory/products/${productId}`,
      (productId) => `/products/inventory/products/${productId}`,
    ),
    adjustVariant: byPanelFn(
      (productId, variantSku) =>
        `/admin/inventory/products/${productId}/variants/${encodeURIComponent(variantSku)}/adjust`,
      (productId, variantSku) =>
        `/products/inventory/products/${productId}/variants/${encodeURIComponent(variantSku)}/adjust`,
    ),
    bulkSetVariants: byPanel(
      "/admin/inventory/variants/bulk-set",
      "/products/inventory/variants/bulk-set",
    ),
    stats: "/admin/inventory/stats",
    lowStock: "/admin/inventory/low-stock",
    transactions: "/admin/inventory/transactions",
    releaseExpiredReservations: "/admin/inventory/reservations/release-expired",
    warehouses: "/admin/inventory/warehouses",
    warehouse: (warehouseId) => `/admin/inventory/warehouses/${warehouseId}`,
    warehouseStatus: "/admin/inventory/warehouses/status",
  },
  payouts: {
    summary: "/sellers/commissions/summary",
    commissions: "/sellers/commissions",
    commissionsExport: "/sellers/commissions/export",
    sellerPayouts: "/sellers/commissions/payouts",
    sellerPayoutsExport: "/sellers/commissions/payouts/export",
    myCommissions: "/sellers/commissions/my-commissions",
    mySummary: "/sellers/commissions/my-summary",
    myPromotionLedger: "/sellers/commissions/my-promotion-ledger",
    promotionLedger: "/sellers/commissions/promotion-ledger",
    myPayouts: "/sellers/commissions/my-payouts",
    myWallet: "/sellers/commissions/my-wallet",
    myPayoutPreference: "/sellers/commissions/my-payout-preference",
    myPayoutsExport: "/sellers/commissions/my-payouts/export",
    mySettlements: "/sellers/commissions/my-settlements",
    mySettlementsExport: "/sellers/commissions/my-settlements/export",
    mySettlementStatement: (settlementId) =>
      `/sellers/commissions/my-settlements/${settlementId}/statement`,
    calculate: (orderId) => `/sellers/commissions/calculate/${orderId}`,
    process: "/sellers/commissions/process-payouts",
    complete: (payoutId) => `/sellers/commissions/payouts/${payoutId}/process`,
    fail: (payoutId) => `/sellers/commissions/payouts/${payoutId}/fail`,
    cancel: (payoutId) => `/sellers/commissions/payouts/${payoutId}/cancel`,
    approve: (payoutId) => `/sellers/commissions/payouts/${payoutId}/approve`,
    hold: (payoutId) => `/sellers/commissions/payouts/${payoutId}/hold`,
    releaseHold: (payoutId) =>
      `/sellers/commissions/payouts/${payoutId}/release-hold`,
    retry: (payoutId) => `/sellers/commissions/payouts/${payoutId}/retry`,
    syncRazorpayX: (payoutId) =>
      `/sellers/commissions/payouts/${payoutId}/sync-razorpayx`,
    operationsQueue: "/sellers/commissions/payout-ops/queue",
    refreshEligibility: "/sellers/commissions/payout-ops/refresh-eligibility",
    sellerWallet: (sellerId) => `/sellers/commissions/wallet/${sellerId}`,
    negativeBalances: "/sellers/commissions/negative-balances",
    resolveNegativeBalance: (settlementId) =>
      `/sellers/commissions/negative-balances/${settlementId}/resolve`,
    settlements: "/sellers/commissions/settlements",
    settlementsExport: "/sellers/commissions/settlements/export",
    settlementStatement: (settlementId) =>
      `/sellers/commissions/settlements/${settlementId}/statement`,
  },
  commerceSettings: {
    detail: "/admin/commerce-settings",
  },
  wallets: {
    adminTransactions: "/wallets/admin/transactions",
  },
  referral: {
    productAmounts: "/admin/referral/product-amounts",
    productAmount: (configId) => `/admin/referral/product-amounts/${configId}`,
    influencers: "/admin/referral/influencers",
    parentInfluencers: "/admin/referral/influencers/parents",
    brandAssociatesByParent: (parentId) =>
      `/admin/referral/influencers/${parentId}/brand-associates`,
    childInfluencers: (parentId) =>
      `/admin/referral/influencers/${parentId}/children`,
    influencerStatus: (influencerId) =>
      `/admin/referral/influencers/${influencerId}/status`,
    influencerProfile: (influencerId) =>
      `/admin/referral/influencers/${influencerId}/profile`,
    influencerChildPermission: (influencerId) =>
      `/admin/referral/influencers/${influencerId}/child-permission`,
    influencerVerification: (influencerId) =>
      `/admin/referral/influencers/${influencerId}/verification`,
    promoteInfluencer: (influencerId) =>
      `/admin/referral/influencers/${influencerId}/promote`,
    codes: "/admin/referral/codes",
    code: (codeId) => `/admin/referral/codes/${codeId}`,
    orders: "/admin/referral/orders",
    commissions: "/admin/referral/commissions",
    payouts: "/admin/referral/payouts",
    approvePayout: (payoutId) => `/admin/referral/payouts/${payoutId}/approve`,
    rejectPayout: (payoutId) => `/admin/referral/payouts/${payoutId}/reject`,
    paidPayout: (payoutId) => `/admin/referral/payouts/${payoutId}/paid`,
    rules: "/admin/referral/rules",
    bonusRules: "/admin/referral/bonus-rules",
    bonusRule: (ruleId) => `/admin/referral/bonus-rules/${ruleId}`,
    evaluateBonusRules: "/admin/referral/bonus-rules/evaluate",
    bonusAchievements: "/admin/referral/bonus-achievements",
    bonusProgress: "/admin/referral/reports/bonus-progress",
    summary: "/admin/referral/reports/summary",
    hierarchy: "/admin/referral/reports/hierarchy",
    fraud: "/admin/referral/fraud",
  },
  tax: {
    adminReports: "/tax/reports",
    adminInvoice: (orderId) => `/tax/orders/${orderId}/invoice`,
    invoice: (orderId) => `/tax/orders/${orderId}/invoice`,
    marketplaceInvoices: (orderId) =>
      `/tax/orders/${orderId}/marketplace-invoices`,
    invoices: "/tax/invoices",
    invoicesExport: "/tax/invoices/export",
    invoiceDetail: (invoiceId) => `/tax/invoices/${invoiceId}`,
    invoiceDownload: (invoiceId) => `/tax/invoices/${invoiceId}/download`,
    invoiceDispatch: (invoiceId) => `/tax/invoices/${invoiceId}/dispatch`,
    creditNotes: "/tax/credit-notes",
    creditNotesExport: "/tax/credit-notes/export",
    creditNoteDownload: (creditNoteId) =>
      `/tax/credit-notes/${creditNoteId}/download`,
    creditNoteDispatch: (creditNoteId) =>
      `/tax/credit-notes/${creditNoteId}/dispatch`,
    reports: "/tax/reports",
    reportsExport: "/tax/reports/export",
    documentDispatches: "/tax/document-dispatches",
    documentDispatchRetry: (dispatchId) =>
      `/tax/document-dispatches/${dispatchId}/retry`,
  },
  coupons: {
    list: "/pricing/coupons",
    detail: (couponId) => `/pricing/coupons/${couponId}`,
  },
  delivery: {
    serviceability: "/delivery/serviceability",
    rates: "/delivery/rates",
    shipments: "/delivery/shipments",
    shipment: (shipmentId) => `/delivery/shipments/${shipmentId}`,
    shipmentTracking: (shipmentId) =>
      `/delivery/shipments/${shipmentId}/tracking`,
  },
  shippingProfiles: {
    list: "/shipping-profiles",
    create: "/shipping-profiles",
    detail: (profileId) => `/shipping-profiles/${profileId}`,
    update: (profileId) => `/shipping-profiles/${profileId}`,
    delete: (profileId) => `/shipping-profiles/${profileId}`,
    bulkDelete: "/shipping-profiles/bulk-delete",
    setDefault: (profileId) => `/shipping-profiles/${profileId}/set-default`,
    templates: "/shipping-profiles/templates",
    template: (templateId) => `/shipping-profiles/templates/${templateId}`,
    cloneTemplate: (templateId) =>
      `/shipping-profiles/templates/${templateId}/clone`,
  },
  returns: {
    list: "/returns",
    detail: (returnId) => `/returns/${returnId}`,
    byOrder: (orderId) => `/returns/order/${orderId}`,
    mine: "/returns/my-returns",
    approve: (returnId) => `/returns/${returnId}/approve`,
    reject: (returnId) => `/returns/${returnId}/reject`,
    schedule: (returnId) => `/returns/${returnId}/schedule`,
    reverseTracking: (returnId) =>
      `/returns/${returnId}/reverse-shipment/tracking`,
    receive: (returnId) => `/returns/${returnId}/receive`,
    qc: (returnId) => `/returns/${returnId}/qc`,
    qcEvidence: (returnId) => `/returns/${returnId}/qc/evidence`,
    qcDecision: (returnId) => `/returns/${returnId}/qc/decision`,
    returnToCustomer: (returnId) => `/returns/${returnId}/return-to-customer`,
    returnToCustomerTracking: (returnId) =>
      `/returns/${returnId}/return-to-customer/tracking`,
    refund: (returnId) => `/returns/${returnId}/refund`,
    retryRefund: (returnId) => `/returns/${returnId}/refund/retry`,
    syncRefund: (returnId) => `/returns/${returnId}/refund/sync`,
    replacement: (returnId) => `/returns/${returnId}/replacement`,
    close: (returnId) => `/returns/${returnId}/close`,
    analytics: "/admin/returns/analytics",
  },
  notifications: {
    mine: "/notifications/me",
    admin: "/notifications/admin",
    send: "/notifications",
    preferences: "/notifications/preferences",
  },
  stockNotifications: {
    list: "/stock-notifications",
    notify: "/stock-notifications/notify",
    notifyBulk: "/stock-notifications/notify/bulk",
  },
  support: {
    mine: "/support/queries",
    myDetail: (queryId) => `/support/queries/${queryId}`,
    create: "/support/queries",
    reply: (queryId) => `/support/queries/${queryId}/replies`,
    adminList: "/admin/support/queries",
    adminDetail: (queryId) => `/admin/support/queries/${queryId}`,
    adminStatus: (queryId) => `/admin/support/queries/${queryId}/status`,
    adminDelete: (queryId) => `/admin/support/queries/${queryId}`,
    adminBulkDelete: "/admin/support/queries",
  },
  deals: {
    list: "/deals",
    detail: (dealId) => `/deals/${dealId}`,
    create: "/deals",
    update: (dealId) => `/deals/${dealId}`,
    submit: (dealId) => `/deals/${dealId}/submit`,
    approve: (dealId) => `/deals/${dealId}/approve`,
    reject: (dealId) => `/deals/${dealId}/reject`,
    pause: (dealId) => `/deals/${dealId}/pause`,
    resume: (dealId) => `/deals/${dealId}/resume`,
    cancel: (dealId) => `/deals/${dealId}/cancel`,
    renew: (dealId) => `/deals/${dealId}/renew`,
    analytics: "/deals/analytics",
    payouts: "/deals/payouts",
    generatePayout: "/deals/payouts/generate",
    processPayout: (payoutId) => `/deals/payouts/${payoutId}/process`,
  },
  analytics: {
    seller: "/analytics",
    events: "/analytics",
    adminDashboard: "/analytics/admin-dashboard",
    sellerDashboard: "/analytics/seller-dashboard",
    realtime: "/admin/analytics/realtime",
    returns: "/admin/returns/analytics",
  },
  operationsReports: {
    orders: "/admin/reports/orders/export",
    products: "/admin/reports/products/export",
    inventory: "/admin/reports/inventory/export",
    shipments: "/admin/reports/shipments/export",
    returns: "/admin/reports/returns/export",
    cancellations: "/admin/reports/cancellations/export",
    refunds: "/admin/reports/refunds/export",
    sellerScorecards: "/admin/reports/seller-scorecards/export",
  },
  rbac: {
    permissionManagementModules: "/rbac/permission-management/modules",
    modules: "/rbac/modules",
    sidebarModules: byPanel(
      "/rbac/modules/sidebar",
      "/sellers/me/sidebar/modules",
    ),
    module: (moduleId) => `/rbac/modules/${moduleId}`,
    moduleStatus: (moduleId) => `/rbac/modules/${moduleId}/status`,
    reorderModules: "/rbac/modules/reorder",
    permissions: "/rbac/permissions",
    permission: (permissionId) => `/rbac/permissions/${permissionId}`,
    roles: "/rbac/roles",
    role: (roleId) => `/rbac/roles/${roleId}`,
    rolePermissions: (roleId) => `/rbac/roles/${roleId}/permissions`,
    rolePermissionsBulk: (roleId) => `/rbac/roles/${roleId}/permissions/bulk`,
    userPermissions: (userId) => `/rbac/users/${userId}/permissions`,
    userEffectivePermissions: (userId) =>
      `/rbac/users/${userId}/permissions/effective`,
    userPermissionCheck: (userId) => `/rbac/users/${userId}/permissions/check`,
    userPermissionsBulk: (userId) => `/rbac/users/${userId}/permissions/bulk`,
    userRoles: (userId) => `/rbac/users/${userId}/roles`,
    userRoleCheck: (userId) => `/rbac/users/${userId}/roles/check`,
    userRolesBulk: (userId) => `/rbac/users/${userId}/roles/bulk`,
  },
  platform: {
    apiKeys: "/admin/platform/api-keys",
    webhooks: "/admin/platform/webhooks",
    featureFlags: "/admin/platform/feature-flags",
    subscriptionPlans: "/admin/platform/subscription-plans",
    subscriptionPlan: (planId) =>
      `/admin/platform/subscription-plans/${planId}`,
    subscriptions: "/admin/platform/subscriptions",
    subscriptionStatus: (subscriptionId) =>
      `/admin/platform/subscriptions/${subscriptionId}/status`,
    categories: "/admin/platform/categories",
    category: (categoryKey) => `/admin/platform/categories/${categoryKey}`,
    categoryAttributes: (categoryKey) =>
      `/admin/categories/${categoryKey}/attributes`,
    productFamilies: "/admin/platform/product-families",
    productFamily: (familyCode) =>
      `/admin/platform/product-families/${familyCode}`,
    productVariants: "/admin/platform/product-variants",
    productVariant: (variantId) =>
      `/admin/platform/product-variants/${variantId}`,
    brands: "/admin/platform/brands",
    brand: (brandId) => `/admin/platform/brands/${brandId}`,
    collections: "/admin/platform/collections",
    collection: (collectionId) => `/admin/platform/collections/${collectionId}`,
    warrantyTemplates: "/admin/platform/warranty-templates",
    warrantyTemplate: (templateId) =>
      `/admin/platform/warranty-templates/${templateId}`,

    dimensions: "/admin/platform/dimensions",
    dimension: (dimensionId) => `/admin/platform/dimensions/${dimensionId}`,
    batches: "/admin/platform/batches",
    batch: (batchId) => `/admin/platform/batches/${batchId}`,
    productOptions: "/admin/platform/product-options",
    productOption: (optionId) => `/admin/platform/product-options/${optionId}`,
    productOptionValues: "/admin/platform/product-option-values",
    productOptionValue: (optionValueId) =>
      `/admin/platform/product-option-values/${optionValueId}`,
    hsnCodes: "/admin/platform/hsn-codes",
    hsnCode: (hsnCode) => `/admin/platform/hsn-codes/${hsnCode}`,
    geography: "/admin/platform/geography",
    geographyDetail: (countryCode) =>
      `/admin/platform/geography/${countryCode}`,
    contentPages: "/admin/cms",
    contentPage: (slug) => `/admin/cms/${slug}`,
    productReviews: byPanel(
      "/admin/platform/product-reviews",
      "/sellers/me/product-reviews",
    ),
    productReviewSummaries: byPanel(
      "/admin/platform/product-review-summaries",
      "/sellers/me/product-review-summaries",
    ),
    productReviewSummaryReviews: (productId) =>
      byPanel(
        `/admin/platform/product-review-summaries/${productId}/reviews`,
        `/sellers/me/product-review-summaries/${productId}/reviews`,
      ),
    sellerProductReviews: "/sellers/me/product-reviews",
    productReview: (reviewId) => `/admin/platform/product-reviews/${reviewId}`,
    sellerProductReview: (reviewId) =>
      `/sellers/me/product-reviews/${reviewId}`,
    createProductReview: "/admin/platform/product-reviews",
    productReviewsBulkAction: "/admin/platform/product-reviews/bulk-action",
    sellerProductReviewsBulkAction: "/sellers/me/product-reviews/bulk-action",
  },
  common: {
    countries: "/admin/common/countries",
    country: (countryId) => `/admin/common/countries/${countryId}`,
    countryStatus: "/admin/common/countries/status",
    states: "/admin/common/states",
    state: (stateId) => `/admin/common/states/${stateId}`,
    stateStatus: "/admin/common/states/status",
    cities: "/admin/common/cities",
    city: (cityId) => `/admin/common/cities/${cityId}`,
    cityStatus: "/admin/common/cities/status",
    zipCodes: "/admin/common/zip-codes",
    zipCode: (zipCodeId) => `/admin/common/zip-codes/${zipCodeId}`,
    zipCodeStatus: "/admin/common/zip-codes/status",
    taxes: "/admin/common/taxes",
    tax: (taxId) => `/admin/common/taxes/${taxId}`,
    taxStatus: "/admin/common/taxes/status",
    subTaxes: "/admin/common/sub-taxes",
    subTax: (subTaxId) => `/admin/common/sub-taxes/${subTaxId}`,
    subTaxStatus: "/admin/common/sub-taxes/status",
    taxRules: "/admin/common/tax-rules",
    taxRule: (taxRuleId) => `/admin/common/tax-rules/${taxRuleId}`,
    taxRuleStatus: "/admin/common/tax-rules/status",
  },
  system: {
    health: "/admin/system/health",
    queues: "/admin/system/queues",
    pauseQueue: (queueName) => `/admin/system/queues/${queueName}/pause`,
    resumeQueue: (queueName) => `/admin/system/queues/${queueName}/resume`,
    deadLetter: "/admin/system/dead-letter",
    retryDeadLetter: (eventId) => `/admin/system/dead-letter/${eventId}/retry`,
    discardDeadLetter: (eventId) =>
      `/admin/system/dead-letter/${eventId}/discard`,
  },
  festivals: {
    adminList: "/admin/festivals",
    adminDetail: (festivalId) => `/admin/festivals/${festivalId}`,
    adminStatus: (festivalId) => `/admin/festivals/${festivalId}/status`,
    adminAnalytics: "/admin/festivals/analytics",
    adminCategories: (festivalId) =>
      `/admin/festivals/${festivalId}/categories`,
    adminCategory: (categoryId) => `/admin/festivals/categories/${categoryId}`,
    adminProductRequests: "/admin/festivals/product-requests",
    adminApproveProduct: (submissionId) =>
      `/admin/festivals/product-requests/${submissionId}/approve`,
    adminRejectProduct: (submissionId) =>
      `/admin/festivals/product-requests/${submissionId}/reject`,
    sellerList: "/seller/festivals",
    sellerJoin: (festivalId) => `/seller/festivals/${festivalId}/join`,
    sellerCategories: (festivalId) =>
      `/seller/festivals/${festivalId}/categories`,
    sellerProducts: (festivalId) => `/seller/festivals/${festivalId}/products`,
    sellerProduct: (submissionId) =>
      `/seller/festivals/products/${submissionId}`,
  },
};
